import { zCustom } from '@nucleus/lib-shape';
import { z } from 'zod';
import { imageSchema } from '../media/image';
import { ColorConfigSchema, ColorPaletteKey } from './colors';
import { PresetMediaKeySchema, PresetMediaSchema } from './media';

// UTILITY TYPES
const AnyObjectSchema = z.record(z.string(), z.unknown());

export const StyleSchema = z
  .object({
    theme: AnyObjectSchema,
  })
  .and(AnyObjectSchema);

export type Style = z.infer<typeof StyleSchema>;

// COLORS
export const ColorPaletteKeys = Object.values(ColorPaletteKey);

export const ColorPaletteKeySchema = z.enum([
  ColorPaletteKey.Color1,
  ColorPaletteKey.Color2,
  ColorPaletteKey.Color3,
  ColorPaletteKey.Color4,
  ColorPaletteKey.Color5,
  ColorPaletteKey.Color6,
  ColorPaletteKey.ColorLight,
  ColorPaletteKey.ColorDark,
]);

export const ColorPaletteColorsSchema = z.object({
  [ColorPaletteKey.Color1]: ColorConfigSchema,
  [ColorPaletteKey.Color2]: ColorConfigSchema,
  [ColorPaletteKey.Color3]: ColorConfigSchema,
  [ColorPaletteKey.Color4]: ColorConfigSchema,
  [ColorPaletteKey.Color5]: ColorConfigSchema,
  [ColorPaletteKey.Color6]: ColorConfigSchema,
  [ColorPaletteKey.ColorLight]: ColorConfigSchema,
  [ColorPaletteKey.ColorDark]: ColorConfigSchema,
});

export type ColorPaletteColors = z.infer<typeof ColorPaletteColorsSchema>;

export const ColorPaletteConfigSchema = z.object({
  createdBy: z.string().optional(),
  lightMode: ColorPaletteColorsSchema,
  sourcePalette: z.string().optional(),
  updatedAt: zCustom.iso().optional(),
});

export type ColorPaletteConfig = z.infer<typeof ColorPaletteConfigSchema>;

export const ColorPaletteSchema = z.object({
  description: z.string().optional(),
  title: z.string().optional(),
  createdAt: zCustom.iso().optional(),
  createdBy: z.string().optional(),
  id: z.string().optional(),
  lightMode: ColorPaletteColorsSchema,
  ownerId: z.string().optional(),
  updatedAt: zCustom.iso().optional(),
  updatedBy: z.string().optional(),
});

export type ColorPalette = z.infer<typeof ColorPaletteSchema>;

// FONTS
export const FontConfigSchema = z.object({
  capitalization: z.enum(['none', 'everyWord', 'firstWord', 'lowercase', 'uppercase']),
  decoration: z.enum(['none', 'underline']),
  fontFamily: z.string(),
  fontSizePx: z.number(),
  fontStyle: z.enum(['normal', 'italic']),
  fontWeight: z.string(),
  forceBold: z.boolean(),
  letterSpacingEm: z.number(),
  lineHeightEm: z.number(),
  source: z.string(),
});

export type FontConfig = z.infer<typeof FontConfigSchema>;

export const FontPaletteKey = {
  Headline1: 'headline1',
  Headline2: 'headline2',
  Headline3: 'headline3',
  Headline4: 'headline4',
  Headline5: 'headline5',
  Headline6: 'headline6',
  Paragraph1: 'paragraph1',
  Paragraph2: 'paragraph2',
  Paragraph3: 'paragraph3',
  Paragraph4: 'paragraph4',
  Title1: 'title1',
  Title2: 'title2',
  Description1: 'description1',
  Description2: 'description2',
  Blockquote1: 'blockquote1',
  Blockquote2: 'blockquote2',
  Label1: 'label1',
  Label2: 'label2',
  Label3: 'label3',
  Label4: 'label4',
  Label5: 'label5',
  Label6: 'label6',
  Nav1: 'navigation1',
  Nav2: 'navigation2',
  Nav3: 'navigation3',
  Nav4: 'navigation4',
  Nav5: 'navigation5',
  Button1: 'button1',
  Button2: 'button2',
  Button3: 'button3',
  Button4: 'button4',
} as const;

export type FontPaletteKey = (typeof FontPaletteKey)[keyof typeof FontPaletteKey];

export const FontPaletteKeys = Object.values(FontPaletteKey);

export const FontPaletteKeySchema = z.enum([
  FontPaletteKey.Headline1,
  FontPaletteKey.Headline2,
  FontPaletteKey.Headline3,
  FontPaletteKey.Headline4,
  FontPaletteKey.Headline5,
  FontPaletteKey.Headline6,
  FontPaletteKey.Paragraph1,
  FontPaletteKey.Paragraph2,
  FontPaletteKey.Paragraph3,
  FontPaletteKey.Paragraph4,
  FontPaletteKey.Title1,
  FontPaletteKey.Title2,
  FontPaletteKey.Description1,
  FontPaletteKey.Description2,
  FontPaletteKey.Blockquote1,
  FontPaletteKey.Blockquote2,
  FontPaletteKey.Label1,
  FontPaletteKey.Label2,
  FontPaletteKey.Label3,
  FontPaletteKey.Label4,
  FontPaletteKey.Label5,
  FontPaletteKey.Label6,
  FontPaletteKey.Nav1,
  FontPaletteKey.Nav2,
  FontPaletteKey.Nav3,
  FontPaletteKey.Nav4,
  FontPaletteKey.Nav5,
  FontPaletteKey.Button1,
  FontPaletteKey.Button2,
  FontPaletteKey.Button3,
  FontPaletteKey.Button4,
]);

export const FontPaletteSchema = z.object({
  [FontPaletteKey.Headline1]: FontConfigSchema,
  [FontPaletteKey.Headline2]: FontConfigSchema,
  [FontPaletteKey.Headline3]: FontConfigSchema,
  [FontPaletteKey.Headline4]: FontConfigSchema,
  [FontPaletteKey.Headline5]: FontConfigSchema,
  [FontPaletteKey.Headline6]: FontConfigSchema,
  [FontPaletteKey.Paragraph1]: FontConfigSchema,
  [FontPaletteKey.Paragraph2]: FontConfigSchema,
  [FontPaletteKey.Paragraph3]: FontConfigSchema,
  [FontPaletteKey.Paragraph4]: FontConfigSchema,
  [FontPaletteKey.Title1]: FontConfigSchema,
  [FontPaletteKey.Title2]: FontConfigSchema,
  [FontPaletteKey.Description1]: FontConfigSchema,
  [FontPaletteKey.Description2]: FontConfigSchema,
  [FontPaletteKey.Blockquote1]: FontConfigSchema,
  [FontPaletteKey.Blockquote2]: FontConfigSchema,
  [FontPaletteKey.Label1]: FontConfigSchema,
  [FontPaletteKey.Label2]: FontConfigSchema,
  [FontPaletteKey.Label3]: FontConfigSchema,
  [FontPaletteKey.Label4]: FontConfigSchema,
  [FontPaletteKey.Label5]: FontConfigSchema,
  [FontPaletteKey.Label6]: FontConfigSchema,
  [FontPaletteKey.Nav1]: FontConfigSchema,
  [FontPaletteKey.Nav2]: FontConfigSchema,
  [FontPaletteKey.Nav3]: FontConfigSchema,
  [FontPaletteKey.Nav4]: FontConfigSchema,
  [FontPaletteKey.Nav5]: FontConfigSchema,
  [FontPaletteKey.Button1]: FontConfigSchema,
  [FontPaletteKey.Button2]: FontConfigSchema,
  [FontPaletteKey.Button3]: FontConfigSchema,
  [FontPaletteKey.Button4]: FontConfigSchema,
});

export type FontPalette = z.infer<typeof FontPaletteSchema>;

export const FontPaletteConfigSchema = z.object({
  baseScale: z.number(),
  scale: z.number(),
  id: z.string(),
  styles: FontPaletteSchema,
  updatedAt: zCustom.iso().optional(),
});

export type FontPaletteConfig = z.infer<typeof FontPaletteConfigSchema>;

export const FontPackSchema = z.object({
  baseScale: z.number(),
  body: z.string(),
  emoji: z.string().optional(),
  featured: z.boolean(),
  headline: z.string(),
  nucleusOptimized: z.boolean(),
  createdAt: zCustom.iso().optional(),
  createdBy: z.string().optional(),
  id: z.string(),
  ownerId: z.string().optional(),
  scale: z.number().optional(),
  styles: z.object({
    [FontPaletteKey.Headline1]: FontConfigSchema.partial(),
    [FontPaletteKey.Headline2]: FontConfigSchema.partial(),
    [FontPaletteKey.Headline3]: FontConfigSchema.partial(),
    [FontPaletteKey.Headline4]: FontConfigSchema.partial(),
    [FontPaletteKey.Headline5]: FontConfigSchema.partial(),
    [FontPaletteKey.Headline6]: FontConfigSchema.partial(),
    [FontPaletteKey.Paragraph1]: FontConfigSchema.partial(),
    [FontPaletteKey.Paragraph2]: FontConfigSchema.partial(),
    [FontPaletteKey.Paragraph3]: FontConfigSchema.partial(),
    [FontPaletteKey.Paragraph4]: FontConfigSchema.partial(),
    [FontPaletteKey.Title1]: FontConfigSchema.partial(),
    [FontPaletteKey.Title2]: FontConfigSchema.partial(),
    [FontPaletteKey.Description1]: FontConfigSchema.partial(),
    [FontPaletteKey.Description2]: FontConfigSchema.partial(),
    [FontPaletteKey.Blockquote1]: FontConfigSchema.partial(),
    [FontPaletteKey.Blockquote2]: FontConfigSchema.partial(),
    [FontPaletteKey.Label1]: FontConfigSchema.partial(),
    [FontPaletteKey.Label2]: FontConfigSchema.partial(),
    [FontPaletteKey.Label3]: FontConfigSchema.partial(),
    [FontPaletteKey.Label4]: FontConfigSchema.partial(),
    [FontPaletteKey.Label5]: FontConfigSchema.partial(),
    [FontPaletteKey.Label6]: FontConfigSchema.partial(),
    [FontPaletteKey.Nav1]: FontConfigSchema.partial(),
    [FontPaletteKey.Nav2]: FontConfigSchema.partial(),
    [FontPaletteKey.Nav3]: FontConfigSchema.partial(),
    [FontPaletteKey.Nav4]: FontConfigSchema.partial(),
    [FontPaletteKey.Nav5]: FontConfigSchema.partial(),
    [FontPaletteKey.Button1]: FontConfigSchema.partial(),
    [FontPaletteKey.Button2]: FontConfigSchema.partial(),
    [FontPaletteKey.Button3]: FontConfigSchema.partial(),
    [FontPaletteKey.Button4]: FontConfigSchema.partial(),
  }),
  updatedAt: zCustom.iso().optional(),
  updatedBy: z.string().optional(),
});

export type FontPack = z.infer<typeof FontPackSchema>;

// BUTTONS
export const ButtonPaletteKey = {
  Button1: 'button1',
  Button2: 'button2',
  Button3: 'button3',
  Button4: 'button4',
} as const;

export const ButtonCornerStyleMap = {
  CornerStyle1: 'cornerStyle1',
  CornerStyle2: 'cornerStyle2',
  CornerStyle3: 'cornerStyle3',
  CornerStyle4: 'cornerStyle4',
} as const;

export const ButtonCornerStyleSchema = z.enum([
  ButtonCornerStyleMap.CornerStyle1,
  ButtonCornerStyleMap.CornerStyle2,
  ButtonCornerStyleMap.CornerStyle3,
  ButtonCornerStyleMap.CornerStyle4,
]);

export type ButtonCornerStyle = z.infer<typeof ButtonCornerStyleSchema>;

export const ButtonCornerStyles = Object.values(ButtonCornerStyleMap);

export const ButtonConfigSchema = z.object({
  cornerStyle: ButtonCornerStyleSchema,
});

export type ButtonConfig = z.infer<typeof ButtonConfigSchema>;

export type ButtonPaletteKey = (typeof ButtonPaletteKey)[keyof typeof ButtonPaletteKey];

export const ButtonPaletteKeys = Object.values(ButtonPaletteKey);

export const ButtonPaletteKeySchema = z.enum([
  ButtonPaletteKey.Button1,
  ButtonPaletteKey.Button2,
  ButtonPaletteKey.Button3,
  ButtonPaletteKey.Button4,
]);

export const ButtonPaletteSchema = z
  .object({
    [ButtonPaletteKey.Button1]: ButtonConfigSchema,
    [ButtonPaletteKey.Button2]: ButtonConfigSchema,
    [ButtonPaletteKey.Button3]: ButtonConfigSchema,
    [ButtonPaletteKey.Button4]: ButtonConfigSchema,
  })
  .partial();

export type ButtonPalette = z.infer<typeof ButtonPaletteSchema>;

export const ButtonPaletteConfigSchema = z.object({
  styles: ButtonPaletteSchema,
});

export type ButtonPaletteConfig = z.infer<typeof ButtonPaletteConfigSchema>;

// LOOK CONFIG
export const LookConfigSchema = z.object({
  colors: ColorPaletteConfigSchema.optional(),
  buttons: ButtonPaletteConfigSchema.optional(),
  fonts: FontPaletteConfigSchema.optional(),
  placeholders: z.record(z.string(), imageSchema).optional(),
  presetMedia: z.record(PresetMediaKeySchema, PresetMediaSchema).optional(),
});

export type LookConfig = z.infer<typeof LookConfigSchema>;

export const LookConfigsSchema = z.record(z.string(), LookConfigSchema);

export type LookConfigs = z.infer<typeof LookConfigsSchema>;
